import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
	collectContactInfo,
	collectContactInfoPayload
} from '../../containers/collectContactInfo/collectContactInfoSlice';

import stateSelectOptions from '../../constants/stateSelectOptions';
import { formatPhoneNumberForDisplay } from '../../utils/formatPhoneNumber';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LoadingButton from '@mui/lab/LoadingButton';

const ContactInformation = ({ handleNext, uuid }) => {
	const dispatch = useDispatch();
	const [contactInformation, setContactInformation] = React.useState({
		uuid,
		firstName: '',
		lastName: '',
		phoneNumber: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: ''
	});

	const [formErrors, setFormErrors] = React.useState({
		firstName: false,
		lastName: false,
		phoneNumber: false,
		address1: false,
		city: false,
		state: false,
		zip: false
	});

	const { hasCompleted, isLoading, error } = useSelector(
		(state) => state.collectContactInfoSlice.collectContactInfo
	);

	React.useEffect(() => {
		if (hasCompleted && !isLoading && !error) {
			handleNext();
		}
	}, [hasCompleted, isLoading, error]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setContactInformation({
			...contactInformation,
			[name]: value
		});
	};

	const handleBlur = (event) => {
		const { name } = event.target;
		if (contactInformation[name] === '') {
			setFormErrors({
				...formErrors,
				[name]: true
			});
		} else {
			setFormErrors({
				...formErrors,
				[name]: false
			});
		}
	};

	const handleSubmitContact = () => {
		let valid = true;
		Object.keys(formErrors).forEach((item) => {
			if (!contactInformation[item].trim()) {
				setFormErrors((prev) => ({
					...prev,
					[item]: true
				}));
				valid = false;
			}
		});
		if (valid) {
			const {
				address1,
				address2,
				city,
				firstName,
				lastName,
				phoneNumber,
				state,
				zip
			} = contactInformation;
			const payload = {
				uuid,
				address1,
				address2,
				city,
				first_name: firstName,
				last_name: lastName,
				phone: phoneNumber.replace(/[^\d]/g, ''),
				state,
				zip
			};
			dispatch(collectContactInfo(payload));
			dispatch(collectContactInfoPayload(payload));
		}
	};
	return (
		<Stack spacing={2}>
			<Typography variant='h5' fontWeight='bold' component='h3'>
				Personal Information
			</Typography>
			<Typography variant='p'>Enter your personal information below</Typography>
			{error && (
				<Typography variant='p' fontSize='small' color='error'>
					There was an error processing your contact info
				</Typography>
			)}
			<TextField
				onChange={handleInputChange}
				name='firstName'
				id='firstName'
				label='First Name'
				size='small'
				fullWidth
				required
				value={contactInformation.firstName}
				onBlur={handleBlur}
				error={formErrors.firstName}
				helperText={formErrors.firstName && 'Enter First Name'}
			/>

			<TextField
				onChange={handleInputChange}
				name='lastName'
				id='lastName'
				label='Last Name'
				size='small'
				fullWidth
				required
				value={contactInformation.lastName}
				onBlur={handleBlur}
				error={formErrors.lastName}
				helperText={formErrors.lastName && 'Enter Last Name'}
			/>

			<TextField
				onChange={handleInputChange}
				name='phoneNumber'
				id='phoneNumber'
				label='Phone Number'
				size='small'
				fullWidth
				required
				value={formatPhoneNumberForDisplay(contactInformation.phoneNumber)}
				onBlur={handleBlur}
				error={formErrors.phoneNumber}
				helperText={formErrors.phoneNumber && 'Enter Phone Number'}
			/>

			<TextField
				onChange={handleInputChange}
				name='address1'
				id='address1'
				label='Address'
				size='small'
				fullWidth
				required
				value={contactInformation.address1}
				onBlur={handleBlur}
				error={formErrors.address1}
				helperText={formErrors.address1 && 'Enter Address'}
			/>

			<TextField
				onChange={handleInputChange}
				name='address2'
				id='address2'
				label='Address 2'
				size='small'
				fullWidth
				value={contactInformation.address2}
			/>
			<Grid container gap={2}>
				<Grid item md={5}>
					<TextField
						onChange={handleInputChange}
						name='city'
						id='city'
						label='City'
						size='small'
						fullWidth
						required
						value={contactInformation.city}
						onBlur={handleBlur}
						error={formErrors.city}
						helperText={formErrors.city && 'Enter City'}
					/>
				</Grid>
				<Grid item md={3}>
					<TextField
						id='contact-info-state'
						name='state'
						label='State'
						required
						fullWidth
						size='small'
						select
						value={contactInformation.state}
						onBlur={handleBlur}
						onChange={handleInputChange}
						error={formErrors.state}
						helperText={formErrors.state && 'Select State'}
					>
						{stateSelectOptions.map((stateItem) => {
							return (
								<MenuItem
									key={stateItem.abbreviation}
									value={stateItem.abbreviation}
									disabled={stateItem.disabled}
								>
									{stateItem.name}
								</MenuItem>
							);
						})}
					</TextField>
				</Grid>
				<Grid item md>
					<TextField
						onChange={handleInputChange}
						name='zip'
						id='zip'
						label='Zip'
						size='small'
						fullWidth
						required
						value={contactInformation.zip}
						onBlur={handleBlur}
						error={formErrors.zip}
						helperText={formErrors.zip && 'Enter Zip Code'}
					/>
				</Grid>
			</Grid>
			<Typography fontSize='0.8em'>
				*RizzBates is currently not available in WI, IA, MN, VT, UT, CT, IL, NJ
			</Typography>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					my: 2
				}}
			>
				<LoadingButton
					variant='outlined'
					color='primary'
					onClick={handleSubmitContact}
					loading={isLoading}
				>
					Next
				</LoadingButton>
			</Box>
		</Stack>
	);
};

ContactInformation.propTypes = {
	handleNext: PropTypes.func,
	uuid: PropTypes.string
};

export default ContactInformation;
